import type { Booking } from '@brighterly/lib-core-types/booking/interfaces'
import type { TutorAction } from '@brighterly/lib-core-types/booking/enums'
import type { Feedback, Tutor } from '@brighterly/lib-core-types/tutor/interfaces'
import type { BookingStore } from './interfaces'
import type { ScopeStatus } from '@utils/stores/scoped-entry'

import { ActivityStatus } from '@brighterly/lib-core-types/booking/enums'

import { useDisciplineStore } from '@stores/discipline'

import { listDemoNoShowStatuses } from '@config/booking'
import { isFullArray } from '@helpers/array'
import { formatBookedDate, formatBookedTime } from '@helpers/date'
import { isBookingTypeDemo, isCompletedBooking, isReservedBooking } from '@utils/booking'
import { getCountdownValue } from '@utils/booking/countdown'
import { entryData, entryStatus } from '@utils/stores/scoped-entry'

const min5 = 300000
const min15 = 900000
const min30 = 1800000
const min60 = 3600000
const hour3 = 10800000

const isAfter5MinToStart = (state: BookingStore): boolean => {
  const value = state.countdown?.totalMilliseconds || 0
  return value <= min5
}

const getActiveId = (): number | null => useDisciplineStore().activeDiscipline?.id ?? null

const isAwaitingEntry = (status: ScopeStatus): boolean => status === 'loading' || status === 'idle'

const getCurrentBooking = (state: BookingStore): Booking | null => entryData(state.currentById, getActiveId())

const getBookings = (state: BookingStore): Booking[] | null => entryData(state.listById, getActiveId())

const getFinishedBookings = (state: BookingStore): Booking[] => entryData(state.finishedById, getActiveId()) || []

const getScheduledBookings = (state: BookingStore): Booking[] => {
  const bookings = getBookings(state) || []
  return bookings.filter(booking => !isReservedBooking(booking))
}

const getReservedBookings = (state: BookingStore): Booking[] => {
  const bookings = getBookings(state) || []
  return bookings.filter(booking => isReservedBooking(booking))
}

const hasCurrentBooking = (state: BookingStore): boolean => !!getCurrentBooking(state)
const hasBookings = (state: BookingStore): boolean => isFullArray(getBookings(state))

const isBookingHasTutor = (state: BookingStore): boolean => {
  const booking = getCurrentBooking(state)
  return !!booking?.tutor?.id
}

const isJoinAllowed = (state: BookingStore): boolean => {
  const booking = getCurrentBooking(state)
  return !!booking?.join_allowed
}

const isTypeDemo = (state: BookingStore): boolean => {
  const booking = getCurrentBooking(state)
  return isBookingTypeDemo(booking || null)
}

const isDemoCompleted = (state: BookingStore): boolean => {
  const booking = getCurrentBooking(state)
  const demo = isTypeDemo(state)
  const completed = isCompletedBooking(booking)
  return demo && completed
}

const isDemoNoShow = (state: BookingStore): boolean => {
  const booking = getCurrentBooking(state)
  const demo = isTypeDemo(state)
  const status = booking?.status || null
  const noShow = status ? listDemoNoShowStatuses.includes(status) : false
  return demo && noShow
}

export const getters = {
  isLoading: (state: BookingStore): boolean => {
    const id = getActiveId()
    return entryStatus(state.currentById, id) === 'loading' || entryStatus(state.listById, id) === 'loading'
  },
  isLoadingDone: (state: BookingStore): boolean => {
    const id = getActiveId()
    return entryStatus(state.currentById, id) === 'ready' || entryStatus(state.listById, id) === 'ready'
  },
  isAllLessonsLoading: (state: BookingStore): boolean => {
    const id = getActiveId()
    return isAwaitingEntry(entryStatus(state.listById, id)) || isAwaitingEntry(entryStatus(state.finishedById, id))
  },
  isLoadingFinished: (state: BookingStore): boolean => entryStatus(state.finishedById, getActiveId()) === 'loading',
  isLoadingFinishedDone: (state: BookingStore): boolean => entryStatus(state.finishedById, getActiveId()) === 'ready',
  getCurrentStatus: (state: BookingStore) => (id: number | null): ScopeStatus => entryStatus(state.currentById, id),
  getListStatus: (state: BookingStore) => (id: number | null): ScopeStatus => entryStatus(state.listById, id),
  getFinishedStatus: (state: BookingStore) => (id: number | null): ScopeStatus => entryStatus(state.finishedById, id),
  hasCurrentBooking: (state: BookingStore): boolean => hasCurrentBooking(state),
  getCurrentBooking: (state: BookingStore): Booking | null => getCurrentBooking(state),
  getCurrentBookingStatus: (state: BookingStore): Booking['status'] | null => getCurrentBooking(state)?.status || null,
  hasBookings: (state: BookingStore): boolean => hasBookings(state),
  getBookings: (state: BookingStore): Booking[] | null => getBookings(state),
  getFinishedBookings: (state: BookingStore): Booking[] => getFinishedBookings(state),
  getScheduledBookings: (state: BookingStore): Booking[] => getScheduledBookings(state),
  getReservedBookings: (state: BookingStore): Booking[] => getReservedBookings(state),
  bookedTime: (state: BookingStore): string | null => {
    if (!hasCurrentBooking(state)) return null
    return getCurrentBooking(state)?.booked_time || null
  },
  getBooking: (state: BookingStore): Booking | null => getCurrentBooking(state),
  getBookingTutor: (state: BookingStore): Tutor | null => getCurrentBooking(state)?.tutor || null,
  getBookingTutorAction: (state: BookingStore): Record<TutorAction, any> | null => getCurrentBooking(state)?.tutor_action || null,
  getLessonUrl: (state: BookingStore): string | null => getCurrentBooking(state)?.lesson_url || null,
  isBooking: (state: BookingStore): boolean => !!getCurrentBooking(state)?.id,
  isJoinAllowed: (state: BookingStore): boolean => isJoinAllowed(state),
  isBookingHasTutor: (state: BookingStore): boolean => isBookingHasTutor(state),
  isCountdown: (state: BookingStore): boolean => !!state.countdown,
  getCountdownDays: (state: BookingStore): number | null => {
    const value = state.countdown?.days
    const valid = typeof value === 'number'
    return valid ? value : null
  },
  getCountdownHours: (state: BookingStore): string | null => getCountdownValue(state.countdown?.hours),
  getCountdownMinutes: (state: BookingStore): string | null => getCountdownValue(state.countdown?.minutes),
  getCountdownSeconds: (state: BookingStore): string | null => getCountdownValue(state.countdown?.seconds),
  isAfter5MinToStart: (state: BookingStore): boolean => isAfter5MinToStart(state),
  isBefore5MinToStart: (state: BookingStore): boolean => {
    const value = state.countdown?.totalMilliseconds || 0
    return value > min5
  },
  isBefore15MinToStart: (state: BookingStore): boolean => {
    const value = state.countdown?.totalMilliseconds
    const valid = typeof value === 'number'
    return valid ? value > min15 : false
  },
  isBefore30MinToStart: (state: BookingStore): boolean => {
    const value = state.countdown?.totalMilliseconds
    const valid = typeof value === 'number'
    return valid ? value > min30 : false
  },
  isBefore60MinToStart: (state: BookingStore): boolean => {
    const value = state.countdown?.totalMilliseconds
    const valid = typeof value === 'number'
    return valid ? value > min60 : false
  },
  isBefore3HoursToStart: (state: BookingStore): boolean => {
    const value = state.countdown?.totalMilliseconds
    const valid = typeof value === 'number'
    return valid ? value > hour3 : false
  },
  isReadyForRedirectToDemoLesson: (state: BookingStore): boolean => {
    return isAfter5MinToStart(state)
      && isBookingHasTutor(state)
      && isJoinAllowed(state)
  },
  isReadyForRedirectToPaidLesson: (state: BookingStore): boolean => {
    return isBookingHasTutor(state) && isJoinAllowed(state)
  },
  isLessonStarted: (state: BookingStore): boolean => {
    const value = state.countdown?.totalMilliseconds
    const valid = typeof value === 'number'
    return valid ? value <= 0 : false
  },
  isFinished: (state: BookingStore): boolean => getCurrentBooking(state)?.is_finished || false,
  isBookingDemoCompleted: (state: BookingStore): boolean => {
    const booking = getCurrentBooking(state)
    return isBookingTypeDemo(booking) && isCompletedBooking(booking)
  },
  isActivityStatusStart: (state: BookingStore): boolean => getCurrentBooking(state)?.activity_status === ActivityStatus.Start,
  getTutorFeedback: (state: BookingStore): Feedback | null => {
    return getCurrentBooking(state)?.tutor_feedback || null
  },
  isModalJoinDemoLesson: (state: BookingStore): BookingStore['modalJoinDemoLesson'] => state.modalJoinDemoLesson,
  isModalJoinPaidLesson: (state: BookingStore): BookingStore['modalJoinPaidLesson'] => state.modalJoinPaidLesson,
  getBookingDate: (state: BookingStore): string => {
    const start = getCurrentBooking(state)?.start || null
    return start ? formatBookedDate(start) : ''
  },
  getBookingTime: (state: BookingStore): string => {
    const start = getCurrentBooking(state)?.start || null
    return start ? formatBookedTime(start) : ''
  },
  getBookingTimezone: (state: BookingStore): string => {
    return getBookings(state)?.[0]?.booked_timezone || ''
  },
  getBookingAbbr: (state: BookingStore): string => {
    return getCurrentBooking(state)?.booked_timezone_abbr || ''
  },
  isTypeDemo: (state: BookingStore): boolean => isBookingTypeDemo(getCurrentBooking(state)),
  isDemoCompleted: (state: BookingStore): boolean => isDemoCompleted(state),
  isDemoNoShow: (state: BookingStore): boolean => isDemoNoShow(state),
  getBookingTrigger: (state: BookingStore): BookingStore['bookingTrigger'] => state.bookingTrigger,
  getIcsLink: (state: BookingStore): BookingStore['icsLink'] => state.icsLink,
  isIcsLink: (state: BookingStore): boolean => !!state.icsLink,
  isIcsProcessing: (state: BookingStore): BookingStore['icsProcessing'] => state.icsProcessing,
}
